import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import './App.css';
import Dashboard from './components/Dashboard';

interface User {
  gid: string;
  name: string;
  photo?: {
    image_128x128: string;
  };
}

function App() {
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [workspaceMembershipId, setWorkspaceMembershipId] = useState<string | null>(null);
  const [userId, setUserId] = useState<string | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [tokenInput, setTokenInput] = useState('');

  useEffect(() => {
    // Check if we have stored credentials
    const storedToken = localStorage.getItem('asana_access_token');
    const storedWorkspaceMembershipId = localStorage.getItem('asana_workspace_membership_id');
    const storedUserId = localStorage.getItem('asana_user_id');
    const storedUser = localStorage.getItem('asana_user');

    if (storedToken && storedWorkspaceMembershipId && storedUserId && storedUser) {
      setAccessToken(storedToken);
      setWorkspaceMembershipId(storedWorkspaceMembershipId);
      setUserId(storedUserId);
      setUser(JSON.parse(storedUser));
    }
    setLoading(false);
  }, []);

  const handleTokenSubmit = async (token: string) => {
    try {
      // Fetch user info to validate the token
      const response = await fetch('https://app.asana.com/api/1.0/users/me', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Invalid token');
      }

      const userData = await response.json();
      const userId = userData.data.gid;

      // Get the user's workspaces
      const workspacesResponse = await fetch('https://app.asana.com/api/1.0/workspaces', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!workspacesResponse.ok) {
        throw new Error('Failed to fetch workspaces');
      }
      const workspacesData = await workspacesResponse.json();
      const workspaceId = workspacesData.data[0].gid; // Use the first workspace for now

      // Get the workspace memberships for the user
      const membershipsResponse = await fetch(`https://app.asana.com/api/1.0/users/${userId}/workspace_memberships?workspace=${workspaceId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!membershipsResponse.ok) {
        throw new Error('Failed to fetch workspace memberships');
      }
      const membershipsData = await membershipsResponse.json();
      const workspaceMembership = membershipsData.data[0]; // Use the first membership for the workspace
      const workspaceMembershipId = workspaceMembership.gid;

      setAccessToken(token);
      setWorkspaceMembershipId(workspaceMembershipId);
      setUserId(userId);
      setUser(userData.data);

      // Store in localStorage
      localStorage.setItem('asana_access_token', token);
      localStorage.setItem('asana_workspace_membership_id', workspaceMembershipId);
      localStorage.setItem('asana_user_id', userId);
      localStorage.setItem('asana_user', JSON.stringify(userData.data));
    } catch (error) {
      console.error('Error validating token:', error);
      alert('Invalid personal access token. Please check your token and try again.');
    }
  };

  const handleDisconnect = () => {
    setAccessToken(null);
    setWorkspaceMembershipId(null);
    setUserId(null);
    setUser(null);
    // Clear localStorage
    localStorage.removeItem('asana_access_token');
    localStorage.removeItem('asana_workspace_membership_id');
    localStorage.removeItem('asana_user_id');
    localStorage.removeItem('asana_user');
  };

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  if (!accessToken || !workspaceMembershipId || !userId || !user) {
    return (
      <div className="auth-container">
        <div className="auth-box">
          <h1>Asana Helper</h1>
          <p>Enter your Asana Personal Access Token to get started</p>
          <div className="token-input-container">
            <input
              type="password"
              placeholder="Enter your personal access token"
              value={tokenInput}
              onChange={(e) => setTokenInput(e.target.value)}
              className="token-input"
            />
            <button 
              onClick={() => handleTokenSubmit(tokenInput)}
              className="connect-button"
              disabled={!tokenInput.trim()}
            >
              Connect
            </button>
          </div>
          <p className="token-help">
            You can create a personal access token in your 
            <a href="https://app.asana.com/0/my-apps" target="_blank" rel="noopener noreferrer">
              Asana Developer Console
            </a>
          </p>
        </div>
      </div>
    );
  }

  return (
    <Router>
      <Routes>
        <Route path="/" element={<Navigate to="/tasks" replace />} />
        <Route 
          path="/tasks" 
          element={
            <Dashboard 
              accessToken={accessToken} 
              workspaceMembershipId={workspaceMembershipId}
              userId={userId}
              user={user} 
              onDisconnect={handleDisconnect}
            />
          } 
        />
        <Route 
          path="/comments" 
          element={
            <Dashboard 
              accessToken={accessToken} 
              workspaceMembershipId={workspaceMembershipId}
              userId={userId}
              user={user} 
              onDisconnect={handleDisconnect}
            />
          } 
        />
      </Routes>
    </Router>
  );
}

export default App; 